import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import notificationService, { NotificationItem, NotificationStats } from '../services/notification';
import authService from '../services/auth';
import apiService from '../services/api';

interface NotificationState {
  unreadCount: number;
  recentNotifications: NotificationItem[];
  stats: NotificationStats | null;
  loading: boolean;
  lastFetchTime: number;
}

interface NotificationContextType {
  state: NotificationState;
  actions: {
    refreshNotifications: () => Promise<void>;
    markAsRead: (notificationIds: number[]) => void;
    markAllAsRead: () => void;
    startPolling: () => void;
    stopPolling: () => void;
  };
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: React.ReactNode;
  pollingInterval?: number; // 轮询间隔，默认30秒
  isAuthenticated: boolean;
  isLoading: boolean;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  pollingInterval = 30000,
  isAuthenticated,
  isLoading,
}) => {
  const [state, setState] = useState<NotificationState>({
    unreadCount: 0,
    recentNotifications: [],
    stats: null,
    loading: false,
    lastFetchTime: 0,
  });

  const pollingTimerRef = useRef<number | null>(null);

  // 刷新通知数据
  const refreshNotifications = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      
      // 并行获取数据：获取最近通知和统计数据（避免重复调用notification-logs）
      const [recentNotifications, apiStats] = await Promise.all([
        notificationService.getRecentNotifications(10).catch(() => []),
        // 直接调用API获取统计数据，避免getNotificationStats中的重复调用
        apiService.get('/api/v1/notification-stats').catch(() => null),
      ]);

      // 为通知添加已读状态
      const notificationsWithReadStatus = recentNotifications.map(notification => ({
        ...notification,
        read_at: notificationService.isNotificationRead(notification.id) 
          ? new Date().toISOString() 
          : null,
      }));

      // 计算未读数量：基于最近1小时的通知
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
      const unreadCount = recentNotifications.filter(notification => {
        const sentAt = new Date(notification.sent_at);
        const isRecent = sentAt > oneHourAgo;
        const isUnread = !notificationService.isNotificationRead(notification.id);
        return isRecent && isUnread;
      }).length;

      const stats = {
        total_sent: apiStats?.total_sent || 0,
        success_rate: apiStats?.success_rate || 0,
        recent_failures: apiStats?.failed_count || 0,
        unread_count: unreadCount,
      };

      setState(prev => ({
        ...prev,
        unreadCount: unreadCount,
        recentNotifications: notificationsWithReadStatus || [],
        stats: stats,
        loading: false,
        lastFetchTime: Date.now(),
      }));
    } catch (error) {
      console.error('刷新通知数据失败:', error);
      setState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  // 标记通知为已读
  const markAsRead = useCallback((notificationIds: number[]) => {
    notificationService.markAsRead(notificationIds);
    
    setState(prev => ({
      ...prev,
      recentNotifications: prev.recentNotifications.map(notification =>
        notificationIds.includes(notification.id)
          ? { ...notification, read_at: new Date().toISOString() }
          : notification
      ),
      unreadCount: Math.max(0, prev.unreadCount - notificationIds.length),
    }));
  }, []);

  // 标记所有通知为已读
  const markAllAsRead = useCallback(() => {
    const unreadNotifications = state.recentNotifications
      .filter(n => !n.read_at)
      .map(n => n.id);
    
    if (unreadNotifications.length > 0) {
      markAsRead(unreadNotifications);
    }
    
    notificationService.markAllAsRead();
    setState(prev => ({
      ...prev,
      unreadCount: 0,
      recentNotifications: prev.recentNotifications.map(notification => ({
        ...notification,
        read_at: notification.read_at || new Date().toISOString(),
      })),
    }));
  }, [state.recentNotifications, markAsRead]);

  // 开始/停止轮询（使用 ref 避免因 state 变化导致的重复 effect 触发）
  const startPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
    }
    pollingTimerRef.current = window.setInterval(() => {
      refreshNotifications();
    }, pollingInterval);
  }, [refreshNotifications, pollingInterval]);

  const stopPolling = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);

  // 统一通过 startPolling/stopPolling 管理轮询，避免重复 setInterval
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      // 检查用户是否需要初始化密码，如果是则不启动通知轮询
      const userStatus = authService.getUserStatus();
      if (userStatus.needsPasswordInit) {
        return;
      }
      
      refreshNotifications();
      startPolling();

      const cleanupTimer = setInterval(() => {
        notificationService.cleanupReadNotifications();
      }, 24 * 60 * 60 * 1000);

      return () => {
        stopPolling();
        clearInterval(cleanupTimer);
      };
    } else if (!isAuthenticated) {
      // 清空状态
      setState({
        unreadCount: 0,
        recentNotifications: [],
        stats: null,
        loading: false,
        lastFetchTime: 0,
      });
    }
  }, [isAuthenticated, isLoading, refreshNotifications, startPolling, stopPolling]);

  // 页面可见性变化时的处理
  useEffect(() => {
    const handleVisibilityChange = () => {
      // 只在用户已认证时处理可见性变化
      if (!isAuthenticated || isLoading) return;
      
      if (document.visibilityState === 'visible') {
        // 页面变为可见时，如果超过1分钟没有刷新，则立即刷新
        const timeSinceLastFetch = Date.now() - state.lastFetchTime;
        if (timeSinceLastFetch > 60000) {
          refreshNotifications();
        }
        startPolling();
      } else {
        // 页面变为不可见时，停止轮询以节省资源
        stopPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, isLoading, refreshNotifications, startPolling, stopPolling, state.lastFetchTime]);

  const contextValue: NotificationContextType = {
    state,
    actions: {
      refreshNotifications,
      markAsRead,
      markAllAsRead,
      startPolling,
      stopPolling,
    },
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

// Hook for using notification context
export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;